import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JobHeader from '../components/careers/JobHeader';
import { FaMapMarkerAlt, FaBuilding, FaArrowLeft, FaFileAlt, FaCheckCircle, FaTimes } from 'react-icons/fa';
import { getJobIcon } from './Carriere'; // we'll adjust Carriere export

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [job, setJob] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [applicantFirstName, setApplicantFirstName] = useState('');
  const [applicantLastName, setApplicantLastName] = useState('');
  const [applicantEmail, setApplicantEmail] = useState('');
  const [applicantPhone, setApplicantPhone] = useState('');
  const [applicantMessage, setApplicantMessage] = useState('');
  const [applicantCV, setApplicantCV] = useState(null);
  const [applicantSource, setApplicantSource] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);

  useEffect(() => {
    const { getJobListings } = require('./Carriere');
    const jobs = getJobListings(t) || [];
    const found = jobs.find(j => String(j.id) === String(id));
    setJob(found || null);
  }, [id, t]);

  useEffect(() => {
    if (job) {
      document.title = `${job.title} | ${t('header.careers')}`;
    }
  }, [job, t]);

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{t('careers.jobNotFound', 'Offre introuvable')}</p>
          <button
            onClick={() => navigate('/carrieres')}
            className="inline-flex items-center px-4 py-2 border border-socabeg-blue text-socabeg-blue rounded-md hover:bg-socabeg-blue/5"
          >
            <FaArrowLeft className="mr-2" />
            {t('careers.backToJobs')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <JobHeader job={job} t={t} />

      <div className="container mx-auto px-4 py-12">

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-8">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl md:text-3xl font-bold text-socabeg-blue">{job.title}</h1>
              <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-socabeg-blue/10 text-socabeg-blue">
                {job.typeLabel}
              </span>
            </div>

            <div className="flex items-center text-gray-600 text-sm space-x-4">
              <div className="flex items-center">
                <FaBuilding className="mr-2 text-socabeg-blue" />
                <span>{job.company}</span>
              </div>
              <div className="flex items-center">
                <FaMapMarkerAlt className="mr-2 text-socabeg-blue" />
                <span>{job.location}</span>
              </div>
            </div>

            {job.description && (
              <div className="mt-6">
                <h2 className="text-xl font-semibold mb-2">{t('careers.jobDetails')}</h2>
                <p className="text-gray-700 leading-relaxed">{job.description}</p>
              </div>
            )}

            {job.requirements && (
              <div>
                <h2 className="text-xl font-semibold mb-3">{t('careers.profile')}</h2>
                <ul className="space-y-2">
                  {job.requirements.map((req, index) => (
                    <li key={index} className="flex items-start text-gray-700">
                      <FaCheckCircle className="text-socabeg-blue mt-1 mr-2 flex-shrink-0" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {job.responsibilities && (
              <div>
                <h2 className="text-xl font-semibold mb-3">{t('careers.missions')}</h2>
                <ul className="space-y-2">
                  {job.responsibilities.map((resp, index) => (
                    <li key={index} className="flex items-start text-gray-700">
                      <FaCheckCircle className="text-socabeg-blue mt-1 mr-2 flex-shrink-0" />
                      <span>{resp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-6">
              <button
                type="button"
                className="bg-socabeg-blue text-white py-3 px-8 rounded-md hover:bg-socabeg-blue-dark transition-colors inline-flex items-center"
                onClick={() => setShowApplicationForm(true)}
              >
                <FaFileAlt className="mr-2" />
                {t('careers.applyNow')}
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="border border-gray-200 rounded-lg p-4 flex items-start space-x-3">
              <div className="w-10 h-10 rounded-full bg-socabeg-blue/10 flex items-center justify-center">
                {getJobIcon(job.title)}
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">{t('careers.contractType', 'Type de contrat')}</p>
                <p className="font-semibold text-socabeg-blue">{job.typeLabel}</p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold mb-2">{job.company}</h3>
              <p className="text-sm text-gray-600 mb-2">{t('careers.companyInfo', 'Société du groupe SOCABEG')}</p>
              <div className="flex items-center text-gray-600 text-sm">
                <FaMapMarkerAlt className="mr-2 text-socabeg-blue" />
                <span>{job.location}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Popup formulaire de candidature */}
      {showApplicationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 relative">
            <button
              type="button"
              onClick={() => setShowApplicationForm(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              aria-label="Fermer le formulaire de candidature"
            >
              <FaTimes size={18} />
            </button>
            <h3 className="text-xl font-semibold mb-4 text-socabeg-blue">
              {t('careers.applyNow')}
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                // TODO: brancher envoi réel (envoi de applicantCV + infos)
                // Exemple : construction d'un FormData avec tous les champs.
                if (applicantCV) {
                  // Exemple d'utilisation :
                  // const formData = new FormData();
                  // formData.append('cv', applicantCV);
                }
                setShowApplicationForm(false);
                setApplicantFirstName('');
                setApplicantLastName('');
                setApplicantEmail('');
                setApplicantPhone('');
                setApplicantMessage('');
                setApplicantCV(null);
                setApplicantSource('');
                setConsentGiven(false);
              }}
              className="space-y-4"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('careers.form.firstName', 'Prénom')}
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-socabeg-blue"
                    value={applicantFirstName}
                    onChange={(e) => setApplicantFirstName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t('careers.form.lastName', 'Nom')}
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-socabeg-blue"
                    value={applicantLastName}
                    onChange={(e) => setApplicantLastName(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('careers.form.email', 'Email')}
                </label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-socabeg-blue"
                  value={applicantEmail}
                  onChange={(e) => setApplicantEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('careers.form.phone', 'Téléphone')}
                </label>
                <input
                  type="tel"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-socabeg-blue"
                  value={applicantPhone}
                  onChange={(e) => setApplicantPhone(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('careers.form.message', 'Message / Motivation')}
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-socabeg-blue"
                  rows={4}
                  value={applicantMessage}
                  onChange={(e) => setApplicantMessage(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('careers.form.cv', 'CV (PDF, DOC, DOCX)')}
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-socabeg-blue/10 file:text-socabeg-blue hover:file:bg-socabeg-blue/20"
                  onChange={(e) => setApplicantCV(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('careers.form.source', 'Comment avez-vous connu SOCABEG ?')}
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-socabeg-blue text-sm"
                  value={applicantSource}
                  onChange={(e) => setApplicantSource(e.target.value)}
                >
                  <option value="">{t('careers.form.source.placeholder', 'Choisir')}</option>
                  <option value="site">{t('careers.form.source.website', 'Site internet SOCABEG')}</option>
                  <option value="reseaux">{t('careers.form.source.social', 'Réseaux sociaux')}</option>
                  <option value="recommandation">{t('careers.form.source.referral', 'Recommandation')}</option>
                  <option value="autre">{t('careers.form.source.other', 'Autre')}</option>
                </select>
              </div>
              <div className="flex items-start space-x-2 text-xs text-gray-600">
                <input
                  id="careers-consent"
                  type="checkbox"
                  className="mt-1"
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.target.checked)}
                  required
                />
                <label htmlFor="careers-consent">
                  {t('careers.form.consent', "J'accepte que SOCABEG traite mes données dans le but de traiter ma candidature.")}
                </label>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => setShowApplicationForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
                >
                  {t('careers.form.cancel', 'Annuler')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-socabeg-blue text-white rounded-md hover:bg-socabeg-blue-dark"
                >
                  {t('careers.form.submit', 'Envoyer la candidature')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetail;
